/**
 * Option edits on matrix questions (M4S-5618). Spec: docs/pdlc/crosstabs-matrix-by-column.md §16-§17.
 *
 * A matrix question is statements × answer choices, and it is edited by dimension, never by one
 * (statement, answer choice) entry: a statement or an answer choice is renamed, hidden or moved,
 * and answer choices merge into nets. Each edit applies to every table of the question, By Row and
 * By Column (E1, E2). Hide is display-only: bases, Top 2 / Bottom 2 / Mean and nets still count a
 * hidden item (E3, E4).
 *
 * Edits name items by the survey's codes, which the Postgres mapper puts on the question as
 * `matrixAxes` (E8). A question without axes (fixtures, test builders, the legacy `grid`) draws from
 * a grid rebuilt from `options` and `matrixOptions`, keyed by label, and never takes edits.
 *
 * Stored edits hold only differences from the survey (E9), and every read and write rebuilds them
 * in one shape: ai_layout is JSONB, which returns object keys ordered by length, and the compute
 * dirty check compares JSON (compute-inputs.ts `isComputeDirty`).
 */

import { isChoiceKeyed, isMatrixQuestion } from '@/lib/crosstab/matrix-orientation';
import {
    matrixCellId,
    type CrosstabMatrixAxisItemDto,
    type CrosstabQuestionDto,
} from '@/lib/crosstab/question-formatter';

/** The longest label a rename keeps, and a stored edit may hold. */
export const MATRIX_EDIT_LABEL_MAX = 500;

/** The most entries a stored list, or a net's members, may hold. */
const LIST_MAX = 1000;

// ── The grid ─────────────────────────────────────────────────────────────────

/** A statement or an answer choice. On a derived grid the key is the label. */
export interface MatrixGridItem {
    key: string;
    label: string;
}

/** A matrix question as statements × answer choices, both in survey order. */
export interface MatrixGrid {
    /** 'axes': built from q.matrixAxes. 'derived': rebuilt without them; keys are labels, and
     *  edits never apply. */
    source: 'axes' | 'derived';
    /** matrix/grid (positional Top 2 / Bottom 2) or intensity scale matrix (numeric). */
    choiceKeyed: boolean;
    statements: MatrixGridItem[];
    choices: MatrixGridItem[];
    /** cells[s][c]: the upper-cased tokens counted for statement s × answer choice c; [] if none. */
    cells: string[][][];
}

/** Null unless isMatrixQuestion(q). */
export function toMatrixGrid(q: CrosstabQuestionDto): MatrixGrid | null {
    if (!isMatrixQuestion(q)) return null;
    const choiceKeyed = isChoiceKeyed(q.questionType);
    return gridFromAxes(q, choiceKeyed) ?? derivedGrid(q, choiceKeyed);
}

function gridItems(items: CrosstabMatrixAxisItemDto[]): MatrixGridItem[] {
    const out: MatrixGridItem[] = [];
    const seen = new Set<string>();
    for (const item of items) {
        // A blank key cannot be stored (parseMatrixEdits rejects it), so it cannot be edited either.
        if (typeof item?.key !== 'string' || !item.key.trim() || seen.has(item.key)) continue;
        seen.add(item.key);
        out.push({ key: item.key, label: typeof item.label === 'string' ? item.label : item.key });
    }
    return out;
}

/**
 * Cell (s, c) is the whole option_id of the first option holding matrixCellId(s, c). The whole
 * list, because dedupeQuestions merges a later wave's tokens into the cell they belong to (spec
 * §17.18).
 */
function gridFromAxes(q: CrosstabQuestionDto, choiceKeyed: boolean): MatrixGrid | null {
    const axes = q.matrixAxes;
    if (!axes || !Array.isArray(axes.statements) || !Array.isArray(axes.choices)) return null;
    const statements = gridItems(axes.statements);
    const choices = gridItems(axes.choices);
    if (!statements.length || !choices.length) return null;

    const optionIdsByToken = new Map<string, string[]>();
    for (const opt of q.options) {
        const ids = opt.option_id.map((id) => id.toUpperCase());
        for (const id of ids) if (!optionIdsByToken.has(id)) optionIdsByToken.set(id, ids);
    }
    const cells = statements.map((s) =>
        choices.map((c) => [
            ...(optionIdsByToken.get(matrixCellId(s.key, c.key).toUpperCase()) ?? []),
        ]),
    );
    return { source: 'axes', choiceKeyed, statements, choices, cells };
}

/**
 * The grid without axes. It never splits or composes an id, so ids such as `MATQ_S1_C1` are safe.
 * One axis is the `matrixOptions` entries, the other the distinct option labels in first-seen
 * order, and cell (label, entry) is the first option with that label overlapping the entry, which
 * is what the producers before M4S-5618's option edits drew. The entries come in survey order: by
 * the first option each overlaps, because `options` is in survey order (postgres-question-mapper
 * builds it choice-major), where the object would list integer-like keys first (E12). An entry
 * that overlaps no option goes last.
 */
function derivedGrid(q: CrosstabQuestionDto, choiceKeyed: boolean): MatrixGrid {
    const entries = Object.entries(q.matrixOptions).map(([label, ids]) => ({
        label,
        ids: new Set(ids.map((id) => id.toUpperCase())),
    }));
    const overlaps = (opt: { option_id: string[] }, ids: Set<string>) =>
        opt.option_id.some((id) => ids.has(id.toUpperCase()));
    const firstOverlap = entries.map((e) => {
        const i = q.options.findIndex((opt) => overlaps(opt, e.ids));
        return i < 0 ? Infinity : i;
    });
    const ordered = entries
        .map((e, i) => ({ e, at: firstOverlap[i], i }))
        .sort((a, b) => (a.at === b.at ? a.i - b.i : a.at - b.at))
        .map(({ e }) => e);

    const labels: string[] = [];
    const labelIndex = new Map<string, number>();
    for (const opt of q.options) {
        if (labelIndex.has(opt.option)) continue;
        labelIndex.set(opt.option, labels.length);
        labels.push(opt.option);
    }
    // byEntry[e][l]: the first option labelled l that overlaps entry e.
    const byEntry = ordered.map((e) => {
        const row: string[][] = labels.map(() => []);
        const filled = new Set<number>();
        for (const opt of q.options) {
            const l = labelIndex.get(opt.option)!;
            if (filled.has(l) || !overlaps(opt, e.ids)) continue;
            filled.add(l);
            row[l] = opt.option_id.map((id) => id.toUpperCase());
        }
        return row;
    });

    const entryItems = ordered.map((e) => ({ key: e.label, label: e.label }));
    const labelItems = labels.map((l) => ({ key: l, label: l }));
    // matrix/grid key matrixOptions by answer choice and label options with the statement; an
    // intensity scale matrix the other way round.
    return choiceKeyed
        ? {
              source: 'derived',
              choiceKeyed,
              statements: labelItems,
              choices: entryItems,
              cells: labels.map((_, s) => ordered.map((_, c) => byEntry[c][s])),
          }
        : {
              source: 'derived',
              choiceKeyed,
              statements: entryItems,
              choices: labelItems,
              cells: byEntry,
          };
}

// ── What is stored ───────────────────────────────────────────────────────────

/** Persisted in ai_layout.state.matrixEdits (E6). */
export interface MatrixItemEdit {
    /** A statement key, an answer-choice key or a net key. */
    key: string;
    /** A rename. Absent means the original (a net: its members' labels joined with " + "). */
    label?: string;
    /** Hidden from the tables (E3). Never on a net. */
    hidden?: true;
    /** A net: the answer-choice keys it combines, two or more, in survey order (E5). */
    net?: string[];
}

export interface MatrixQuestionEdits {
    /** Every statement in display order. Absent when the statements are as surveyed. */
    statements?: MatrixItemEdit[];
    /** Every answer choice and net in display order. Absent when unedited and without nets. */
    choices?: MatrixItemEdit[];
}

/** Upper-cased qid → edits. Only edited questions, keys sorted. */
export type MatrixEditsMap = Record<string, MatrixQuestionEdits>;

/**
 * The one builder of a stored entry. Every entry this module returns comes from here, in one field
 * order, so equal edits stringify alike whatever order the record came back in.
 */
function entry(key: string, label?: string, hidden?: boolean, net?: string[]): MatrixItemEdit {
    const out: MatrixItemEdit = { key };
    if (label !== undefined) out.label = label;
    if (hidden && !net) out.hidden = true;
    if (net) out.net = [...net];
    return out;
}

/** The one builder of a question's edits: statements, then choices; null when both are empty. */
function questionEdits(
    statements: MatrixItemEdit[],
    choices: MatrixItemEdit[],
): MatrixQuestionEdits | null {
    if (!statements.length && !choices.length) return null;
    const out: MatrixQuestionEdits = {};
    if (statements.length) out.statements = statements;
    if (choices.length) out.choices = choices;
    return out;
}

/** A fresh map with its keys in plain sort order, as pruneByColumnOverrides sorts. */
function sortedMap(map: MatrixEditsMap): MatrixEditsMap {
    const out: MatrixEditsMap = {};
    for (const qid of Object.keys(map).sort()) out[qid] = map[qid];
    return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        Object.prototype.toString.call(value) === '[object Object]'
    );
}

function parseLabel(raw: unknown): string | undefined {
    if (typeof raw !== 'string') return undefined;
    const label = raw.trim();
    return label && label.length <= MATRIX_EDIT_LABEL_MAX ? label : undefined;
}

/** Two or more distinct non-empty keys, or undefined. */
function parseNet(raw: unknown): string[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const members: string[] = [];
    const seen = new Set<string>();
    for (const key of raw) {
        if (members.length >= LIST_MAX) break;
        if (typeof key !== 'string' || !key.trim() || seen.has(key)) continue;
        seen.add(key);
        members.push(key);
    }
    return members.length >= 2 ? members : undefined;
}

function parseList(raw: unknown, allowNets: boolean): MatrixItemEdit[] {
    if (!Array.isArray(raw)) return [];
    const out: MatrixItemEdit[] = [];
    const seen = new Set<string>();
    for (const item of raw) {
        if (out.length >= LIST_MAX) break;
        if (!isPlainObject(item)) continue;
        const { key } = item;
        if (typeof key !== 'string' || !key.trim() || seen.has(key)) continue;
        let net: string[] | undefined;
        if (item.net != null) {
            // Only answer choices merge (E2), and a net needs two members.
            if (!allowNets) continue;
            net = parseNet(item.net);
            if (!net) continue;
        }
        seen.add(key);
        out.push(entry(key, parseLabel(item.label), item.hidden === true, net));
    }
    return out;
}

/**
 * Untrusted JSON (the record, a POST body, the browser slot) to one question's well-formed edits,
 * or null. Never throws. It cannot check the edits against the question, which the server does
 * not have; the client does that on Save (toMatrixEdits), and the compute on read.
 */
export function parseMatrixQuestionEdits(raw: unknown): MatrixQuestionEdits | null {
    if (!isPlainObject(raw)) return null;
    return questionEdits(parseList(raw.statements, false), parseList(raw.choices, true));
}

/** Untrusted JSON (the record, a POST body, the browser slot) to a well-formed map. Never throws. */
export function parseMatrixEdits(raw: unknown): MatrixEditsMap {
    const out: MatrixEditsMap = {};
    if (!isPlainObject(raw)) return out;
    // Sorted first, so that of two keys naming one qid ("q1", "Q1") the same one always wins.
    for (const rawQid of Object.keys(raw).sort()) {
        const qid = rawQid.trim().toUpperCase();
        if (!qid || Object.prototype.hasOwnProperty.call(out, qid)) continue;
        const edits = parseMatrixQuestionEdits(raw[rawQid]);
        if (edits) out[qid] = edits;
    }
    return sortedMap(out);
}

/** Writes one question's edits; null deletes them. Upper-cases the qid and sorts the keys. */
export function withMatrixEdits(
    map: MatrixEditsMap | undefined,
    qid: string,
    edits: MatrixQuestionEdits | null,
): MatrixEditsMap {
    const QID = qid.trim().toUpperCase();
    const out = parseMatrixEdits(map);
    delete out[QID];
    const parsed = parseMatrixQuestionEdits(edits);
    if (QID && parsed) out[QID] = parsed;
    return sortedMap(out);
}

// ── The view ─────────────────────────────────────────────────────────────────

/** One item in either dialog list; one table or one row in the compute. */
export interface MatrixViewItem {
    key: string;
    /** What the table and the dialog show. */
    label: string;
    /** The survey label. For a net, its members' labels joined with " + ". */
    original: string;
    hidden: boolean;
    /** Position in grid.statements or grid.choices. Absent on a net. */
    index?: number;
    /** A net only: the grid.choices positions it combines, ascending. */
    members?: number[];
    /** A net only: stored member keys this grid does not have, kept so a Save writes them back. */
    extraMembers?: string[];
}

export interface MatrixView {
    /** Display order, hidden items included. */
    statements: MatrixViewItem[];
    /** Answer choices and nets in display order, hidden items included. */
    choices: MatrixViewItem[];
    /** Stored entries this grid cannot resolve (an unknown key, or a net with fewer than two
     *  known members), verbatim and in stored order. Never drawn or shown; written back on Save
     *  so that a session on another wave's codes does not erase them (17.16). */
    carried: { statements: MatrixItemEdit[]; choices: MatrixItemEdit[] };
}

function surveyItems(items: MatrixGridItem[]): MatrixViewItem[] {
    return items.map((item, index) => ({
        key: item.key,
        label: item.label,
        original: item.label,
        hidden: false,
        index,
    }));
}

/** A net's default label: its members' labels as shown, in survey order. */
function netOriginal(members: number[], list: MatrixViewItem[]): string {
    const labelOf = new Map<number, string>();
    for (const item of list) if (item.index !== undefined) labelOf.set(item.index, item.label);
    return members.flatMap((m) => (labelOf.has(m) ? [labelOf.get(m)!] : [])).join(' + ');
}

function resolveList(
    items: MatrixGridItem[],
    stored: MatrixItemEdit[] | undefined,
    carried: MatrixItemEdit[],
): MatrixViewItem[] {
    if (!stored) return surveyItems(items);
    const indexOf = new Map(items.map((item, i) => [item.key, i]));
    const placed = new Set<number>();
    const out: MatrixViewItem[] = [];
    const nets: { item: MatrixViewItem; renamed?: string }[] = [];
    for (const e of stored) {
        if (e.net) {
            // A net may not take an answer choice's key; it would read as that choice.
            if (indexOf.has(e.key)) continue;
            const known = [
                ...new Set(e.net.filter((k) => indexOf.has(k)).map((k) => indexOf.get(k)!)),
            ];
            if (known.length < 2) {
                carried.push(entry(e.key, e.label, false, e.net));
                continue;
            }
            const extraMembers = e.net.filter((k) => !indexOf.has(k));
            const item: MatrixViewItem = {
                key: e.key,
                label: '',
                original: '',
                hidden: false,
                members: known.sort((a, b) => a - b),
                ...(extraMembers.length ? { extraMembers } : {}),
            };
            nets.push({ item, renamed: e.label });
            out.push(item);
            continue;
        }
        const index = indexOf.get(e.key);
        if (index === undefined) {
            carried.push(entry(e.key, e.label, e.hidden));
            continue;
        }
        if (placed.has(index)) continue;
        placed.add(index);
        const original = items[index].label;
        out.push({
            key: e.key,
            label: e.label ?? original,
            original,
            hidden: e.hidden === true,
            index,
        });
    }
    // Items the question has gained since the Save, at the end in survey order (E9).
    for (const item of surveyItems(items)) if (!placed.has(item.index!)) out.push(item);
    // A net that was never renamed follows its members' renames.
    for (const { item, renamed } of nets) {
        item.original = netOriginal(item.members!, out);
        item.label = renamed ?? item.original;
    }
    // A list with nothing shown would draw nothing: show everything instead, order and labels
    // kept. Only a stored edit can get here (the dialog keeps one item shown); a net is shown.
    return out.length && out.every((item) => item.hidden)
        ? out.map((item) => ({ ...item, hidden: false }))
        : out;
}

/** How the question draws: its stored edits, validated and sanitised against the grid (E9). */
export function resolveMatrixView(grid: MatrixGrid, edits: unknown): MatrixView {
    const carried: MatrixView['carried'] = { statements: [], choices: [] };
    // A derived grid's keys are labels, never the survey codes an edit names.
    const stored = grid.source === 'axes' ? parseMatrixQuestionEdits(edits) : null;
    return {
        statements: resolveList(grid.statements, stored?.statements, carried.statements),
        choices: resolveList(grid.choices, stored?.choices, carried.choices),
        carried,
    };
}

function isNet(item: MatrixViewItem): boolean {
    return item.members !== undefined;
}

/** Whether a view list draws differently from the survey: order, a label, a hide or a net. */
function listDiffers(items: MatrixGridItem[], list: MatrixViewItem[]): boolean {
    return (
        list.length !== items.length ||
        list.some(
            (item, i) =>
                isNet(item) || item.index !== i || item.hidden || item.label !== item.original,
        )
    );
}

/** The canonical edits a view holds, or null when it is the question as surveyed (E9). */
export function toMatrixEdits(grid: MatrixGrid, view: MatrixView): MatrixQuestionEdits | null {
    const write = (items: MatrixGridItem[], list: MatrixViewItem[], carried: MatrixItemEdit[]) =>
        listDiffers(items, list) || carried.length
            ? [
                  ...list.map((item) =>
                      entry(
                          item.key,
                          item.label !== item.original ? item.label : undefined,
                          item.hidden,
                          item.members && [
                              ...item.members.flatMap((m) => (items[m] ? [items[m].key] : [])),
                              ...(item.extraMembers ?? []),
                          ],
                      ),
                  ),
                  ...carried.map((e) => entry(e.key, e.label, e.hidden, e.net)),
              ]
            : [];
    return questionEdits(
        write(grid.statements, view.statements, view.carried?.statements ?? []),
        write(grid.choices, view.choices, view.carried?.choices ?? []),
    );
}

/** Whether the question draws differently from the survey: what the rail dot shows. Carried
 *  entries do not count, because nothing of them is drawn. */
export function hasVisibleMatrixEdits(grid: MatrixGrid | null, edits: unknown): boolean {
    if (!grid || grid.source !== 'axes') return false;
    const view = resolveMatrixView(grid, edits);
    return listDiffers(grid.statements, view.statements) || listDiffers(grid.choices, view.choices);
}

// ── The dialog's list operations (E13). Pure; each returns a new list. ───────

/** Hidden items drop to the bottom; a shown one returns above the first hidden item (MSWD-5).
 *  A no-op on a net, which is never hidden. */
export function setHidden(list: MatrixViewItem[], key: string, hidden: boolean): MatrixViewItem[] {
    const at = list.findIndex((item) => item.key === key);
    if (at < 0 || isNet(list[at]) || list[at].hidden === hidden) return [...list];
    const next = list.filter((_, i) => i !== at);
    const moved = { ...list[at], hidden };
    const firstHidden = hidden ? -1 : next.findIndex((item) => item.hidden);
    if (firstHidden < 0) next.push(moved);
    else next.splice(firstHidden, 0, moved);
    return next;
}

/** At most MATRIX_EDIT_LABEL_MAX code units, never splitting a surrogate pair. */
function capLabel(text: string): string {
    if (text.length <= MATRIX_EDIT_LABEL_MAX) return text;
    const code = text.charCodeAt(MATRIX_EDIT_LABEL_MAX - 1);
    const end =
        code >= 0xd800 && code <= 0xdbff ? MATRIX_EDIT_LABEL_MAX - 1 : MATRIX_EDIT_LABEL_MAX;
    return text.slice(0, end);
}

/** Trims, caps at 500 characters (the parse limit); blank restores the original. */
export function renameItem(list: MatrixViewItem[], key: string, text: string): MatrixViewItem[] {
    // Trimmed again after the cap, so what a Save stores is what a reload parses back.
    const label = capLabel(text.trim()).trim();
    const renamed = list.map((item) =>
        item.key === key ? { ...item, label: label || item.original } : item,
    );
    // A net never renamed follows its members' labels, as it will once saved (resolveList).
    return renamed.map((item) => {
        if (!isNet(item)) return item;
        const original = netOriginal(item.members!, renamed);
        return { ...item, original, label: item.label === item.original ? original : item.label };
    });
}

/** One net from the selected answer choices and nets, placed above the first hidden item.
 *  `avoidKeys` are keys the new net must not take (the view's carried choices). */
export function mergeIntoNet(
    choices: MatrixViewItem[],
    keys: string[],
    avoidKeys: string[] = [],
): MatrixViewItem[] {
    const selected = choices.filter((item) => keys.includes(item.key));
    if (selected.length < 2) return [...choices];
    const members = [
        ...new Set(
            selected.flatMap(
                (item) => item.members ?? (item.index !== undefined ? [item.index] : []),
            ),
        ),
    ].sort((a, b) => a - b);
    const extraMembers = [...new Set(selected.flatMap((item) => item.extraMembers ?? []))];
    const used = new Set([...choices.map((item) => item.key), ...avoidKeys]);
    let n = 1;
    while (used.has(`NET${n}`)) n++;
    const original = netOriginal(members, choices);
    const net: MatrixViewItem = {
        key: `NET${n}`,
        label: original,
        original,
        hidden: false,
        members,
        ...(extraMembers.length ? { extraMembers } : {}),
    };
    // The selected nets fold into the new one; the plain answer choices stay.
    const kept = choices.filter((item) => !(isNet(item) && keys.includes(item.key)));
    const firstHidden = kept.findIndex((item) => item.hidden);
    if (firstHidden < 0) return [...kept, net];
    return [...kept.slice(0, firstHidden), net, ...kept.slice(firstHidden)];
}

/** Discards a net, its extraMembers included: that is what the user asked for. */
export function removeNet(choices: MatrixViewItem[], key: string): MatrixViewItem[] {
    return choices.filter((item) => !(item.key === key && isNet(item)));
}

/** True when `key` is the only shown item of its list (a net counts as shown). Hide and
 *  Delete net are disabled on it, so a list never ends with nothing shown. */
export function isLastShown(list: MatrixViewItem[], key: string): boolean {
    const item = list.find((i) => i.key === key);
    return !!item && !item.hidden && list.filter((i) => !i.hidden).length === 1;
}

/** Reset (E13): the question's own statements and answer choices, keeping what is carried. */
export function resetMatrixView(grid: MatrixGrid, view: MatrixView): MatrixView {
    return { ...resolveMatrixView(grid, null), carried: view.carried };
}
